import React from 'react';
import { GioChecklistItem, getChecklistProgress } from '../../types/gioTypes';

interface GioChecklistProps {
    items: GioChecklistItem[];
    onChange: (items: GioChecklistItem[]) => void;
    onAddCustom?: (label: string) => void;
    showAddButton?: boolean;
}

const GioChecklist: React.FC<GioChecklistProps> = ({
    items,
    onChange,
    onAddCustom,
    showAddButton = true,
}) => {
    const [newItemLabel, setNewItemLabel] = React.useState('');
    const { completed, total, percentage } = getChecklistProgress(items);

    const handleToggle = (id: string) => {
        const updated = items.map(item =>
            item.id === id ? { ...item, checked: !item.checked } : item
        );
        onChange(updated);
    };

    const handleRemove = (id: string) => {
        const updated = items.filter(item => item.id !== id);
        onChange(updated);
    };

    const handleAddCustom = () => {
        if (!newItemLabel.trim()) return;

        const newItem: GioChecklistItem = {
            id: `custom_${Date.now()}`,
            label: newItemLabel.trim(),
            checked: false,
            isCustom: true,
        };

        onChange([...items, newItem]);
        setNewItemLabel('');
        onAddCustom?.(newItemLabel.trim());
    };

    return (
        <div className="space-y-3">
            {/* Progress Header */}
            <div className="flex items-center justify-between">
                <span className="text-sm text-white/80 font-medium">
                    📋 Chuẩn bị ({completed}/{total})
                </span>
                <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-white/20 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-green-500 transition-all duration-300"
                            style={{ width: `${percentage}%` }}
                        />
                    </div>
                    <span className="text-xs text-white/60">{percentage}%</span>
                </div>
            </div>

            {/* Checklist Items */}
            <div className="space-y-2">
                {items.map(item => (
                    <div
                        key={item.id}
                        className="flex items-center gap-3 group"
                    >
                        <button
                            type="button"
                            onClick={() => handleToggle(item.id)}
                            className={`
                                w-5 h-5 rounded border-2 flex items-center justify-center
                                transition-all duration-200
                                ${item.checked
                                    ? 'bg-green-500 border-green-500'
                                    : 'bg-transparent border-white/40 hover:border-white/60'
                                }
                            `}
                        >
                            {item.checked && (
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                            )}
                        </button>

                        <span className={`
                            flex-1 text-sm transition-all
                            ${item.checked ? 'text-white/50 line-through' : 'text-white'}
                        `}>
                            {item.label}
                        </span>

                        {item.isCustom && (
                            <button
                                type="button"
                                onClick={() => handleRemove(item.id)}
                                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/30 rounded transition-all"
                            >
                                <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>
                ))}
            </div>

            {/* Add Custom Item */}
            {showAddButton && (
                <div className="flex gap-2 mt-3">
                    <input
                        type="text"
                        value={newItemLabel}
                        onChange={(e) => setNewItemLabel(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddCustom()}
                        placeholder="Thêm mục khác..."
                        className="flex-1 px-3 py-2 bg-white/10 rounded-lg text-white text-sm 
                                   placeholder:text-white/40 border border-white/10 
                                   focus:border-white/30 focus:outline-none"
                    />
                    <button
                        type="button"
                        onClick={handleAddCustom}
                        disabled={!newItemLabel.trim()}
                        className="px-3 py-2 bg-white/20 hover:bg-white/30 rounded-lg 
                                   text-white text-sm transition-colors
                                   disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        + Thêm
                    </button>
                </div>
            )}
        </div>
    );
};

export default GioChecklist;
